import { useState, useEffect } from 'react';
import { Stack, Box, Typography, Grid, Chip, Popover, IconButton } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { ArrowRightAlt, CalendarMonth } from '@mui/icons-material';
import chaletApi from 'api/chaletApi';
import { useAppSelector } from 'redux/hook';
import { ChartStatistics } from './ChartStatistics';
import { BestProductsCards } from './BestProductsCards';
import { InformationCards } from './InformationCards';
import { RatingCard } from './RatingCard';
import { useMediaQuery } from '@mui/material';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, startOfYear, endOfYear } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { OrdersInRange } from 'ui/components/partials/Order/OrdersInRange';

export const Dashboard = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs(startOfMonth(today)));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs(endOfMonth(today)));
  const [dateRange, setDateRange] = useState('Mes');
  const [customRangeAnchor, setCustomRangeAnchor] = useState<HTMLElement | null>(null);
  const { user } = useAppSelector((state) => state.user);
  const [getDashboard, result] = chaletApi.useLazyGetDashboardQuery();

  useEffect(() => {
    if (user && fromDate && toDate) {
      const branch = user.branchWork.branch;
      getDashboard({ id: branch.id, from: fromDate, to: toDate });
    }
  }, [user, fromDate, toDate, getDashboard]);

  const handleDateRangeChange = (value: string) => {
    let newFromDate, newToDate;

    switch (value) {
      case 'Hoy':
        newFromDate = startOfDay(today);
        newToDate = endOfDay(today);
        break;
      case 'Semana':
        newFromDate = startOfWeek(today, { weekStartsOn: 1 });
        newToDate = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'Mes':
        newFromDate = startOfMonth(today);
        newToDate = endOfMonth(today);
        break;
      case 'Año':
        newFromDate = startOfYear(today);
        newToDate = endOfYear(today);
        break;
      default:
        break;
    }
    setFromDate(dayjs(newFromDate));
    setToDate(dayjs(newToDate));
    setDateRange(value);
  };

  const handleCustomRange = (event: React.MouseEvent<HTMLElement>) => {
    setCustomRangeAnchor(event.currentTarget);
  };

  const handleCustomRangeClose = () => {
    setCustomRangeAnchor(null);
  };

  const handleCustomDateChange = () => {
    setDateRange('Personalizado');
    handleCustomRangeClose();
  };

  const dateRangeOptions = [
    { label: 'Hoy', value: 'Hoy' },
    { label: 'Esta semana', value: 'Semana' },
    { label: 'Este mes', value: 'Mes' },
    { label: 'Este año', value: 'Año' }
  ];

  return (
    <Box p={2}>
      <Grid container alignItems="center" justifyContent="center" spacing={2}>
        <Grid item xs={12} sm={5}>
          <Typography
            sx={{
              fontWeight: '800',
              fontSize: '40px',
              textAlign: { xs: 'center', sm: 'left' }
            }}>
            Panel de Control
          </Typography>
        </Grid>
        <Grid item xs={12} sm={7}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="center"
            alignItems="center"
            spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {dateRangeOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  variant={dateRange === option.value ? 'filled' : 'outlined'}
                  color={dateRange === option.value ? 'primary' : 'default'}
                  onClick={() => handleDateRangeChange(option.value)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
              <Chip
                label="Personalizado"
                variant={dateRange === 'Personalizado' ? 'filled' : 'outlined'}
                color={dateRange === 'Personalizado' ? 'primary' : 'default'}
                onClick={handleCustomRange}
                icon={<CalendarMonth />}
                sx={{ cursor: 'pointer' }}
              />
            </Stack>

            <Popover
              open={Boolean(customRangeAnchor)}
              anchorEl={customRangeAnchor}
              onClose={handleCustomRangeClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}>
              <Box p={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Seleccionar rango personalizado
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePicker
                    label="Fecha inicial"
                    value={fromDate}
                    onChange={(newValue) => {
                      const start = dayjs(newValue).toDate();
                      setFromDate(dayjs(startOfDay(start)));
                      handleCustomDateChange();
                    }}
                    sx={{ minWidth: '140px' }}
                  />
                  <ArrowRightAlt />
                  <DatePicker
                    label="Fecha final"
                    value={toDate}
                    onChange={(newValue) => {
                      const end = dayjs(newValue).toDate();
                      setToDate(dayjs(endOfDay(end)));
                      handleCustomDateChange();
                    }}
                    sx={{ minWidth: '140px' }}
                  />
                </Stack>
              </Box>
            </Popover>
          </Stack>
        </Grid>
      </Grid>

      {fromDate && toDate && result.data && result.data.data && (
        <>
          <InformationCards
            ordersCount={result.data.data.ordersCount}
            completedCount={result.data.data.completedCount}
            canceledCount={result.data.data.canceledCount}
            inProgressCount={result.data.data.inProgressCount}
            avgPrepMinutes={result.data.data.avgPrepMinutes}
            grossSales={result.data.data.grossSales}
            netSales={result.data.data.netSales}
            avgOrderValue={result.data.data.avgOrderValue}
            avgItemsPerOrder={result.data.data.avgItemsPerOrder}
            uniqueClients={result.data.data.uniqueClients}
          />

          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={8}>
              <ChartStatistics salesByDay={result.data.data.salesByDay} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <BestProductsCards
                products={result.data.data.mostSoldProducts}
                from={fromDate}
                to={toDate}
                branchId={user.branchWork.branch.id}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};
